import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Loader2, Send, FileWarning } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';

const eur = (v) => Number(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const LEVEL_STYLE = {
  1: 'bg-amber-100 text-amber-800 border-amber-300',
  2: 'bg-orange-100 text-orange-800 border-orange-300',
  3: 'bg-red-100 text-red-800 border-red-300',
};

const Row = ({ label, value, strong, testId }) => (
  <div className="flex justify-between gap-4 text-sm py-1">
    <span className="text-gray-500">{label}</span>
    <span className={strong ? 'font-bold text-[#9B1B30]' : 'font-medium'} data-testid={testId}>{value}</span>
  </div>
);

export const DunningDialog = ({ doc, open, onOpenChange, onCreated, onSendEmail }) => {
  const [preview, setPreview] = useState(null);
  const [params, setParams] = useState({ b2b: null, payment_days: null, due_date: '' });
  const [texts, setTexts] = useState({ intro: '', notice: '', edited: false });
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const previewRef = useRef(null);

  useEffect(() => {
    if (open) {
      setParams({ b2b: null, payment_days: null, due_date: '' });
      setTexts({ intro: '', notice: '', edited: false });
      setCreated(null);
      setPreview(null);
      previewRef.current = null;
    }
  }, [open, doc?.id]);

  useEffect(() => {
    if (!open || !doc) return;
    const q = new URLSearchParams();
    if (params.b2b !== null) q.append('b2b', params.b2b);
    if (params.payment_days !== null && params.payment_days !== '') q.append('payment_days', params.payment_days);
    if (params.due_date) q.append('due_date', params.due_date);
    setLoading(true);
    api.get(`/admin/documents/${doc.id}/dunning-preview?${q.toString()}`)
      .then(({ data }) => {
        const prev = previewRef.current;
        const rate = (p) => p.interest_rate.toFixed(2).replace('.', ',');
        const swap = (s) => (prev
          ? s.split(prev.deadline).join(data.deadline).split(prev.due_date).join(data.due_date).split(`${rate(prev)} %`).join(`${rate(data)} %`)
          : s);
        setTexts((t) => (t.edited
          ? { ...t, intro: swap(t.intro), notice: swap(t.notice) }
          : { intro: data.default_intro, notice: data.default_notice, edited: false }));
        previewRef.current = data;
        setPreview(data);
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || 'Mahnung kann nicht erstellt werden');
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open, doc, params, onOpenChange]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/admin/documents/${doc.id}/create-dunning`, {
        b2b: preview.b2b,
        payment_days: preview.payment_days,
        due_date: preview.due_date,
        intro_text: texts.intro,
        notice_text: texts.notice,
      });
      toast.success(data.message);
      setCreated(data.document);
      onCreated?.(data.document);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mahnung konnte nicht erstellt werden');
    } finally {
      setCreating(false);
    }
  };

  const level = preview?.level;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dunning-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileWarning className="w-5 h-5 text-[#9B1B30]" />
            Mahnung erstellen
          </DialogTitle>
        </DialogHeader>

        {loading && !preview && (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#9B1B30]" /></div>
        )}

        {preview && created && (
          <div className="space-y-4" data-testid="dunning-created">
            <div className="rounded-lg border border-green-300 bg-green-50 p-4 text-sm text-green-800">
              <strong>{created.dunning_level}. Mahnung {created.document_number}</strong> wurde erstellt und im Archiv gespeichert.
              Die Rechnung {preview.invoice_number} hat jetzt den Status „Gemahnt".
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} data-testid="dunning-close-btn">Schließen</Button>
              <Button className="flex-1 bg-[#9B1B30] hover:bg-[#7a1526] text-white" onClick={() => { onOpenChange(false); onSendEmail?.(created); }} data-testid="dunning-send-email-btn">
                <Send className="w-4 h-4 mr-2" /> Per E-Mail senden
              </Button>
            </div>
          </div>
        )}

        {preview && !created && (
          <div className="space-y-5">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-3 py-1 rounded-full border text-sm font-semibold ${LEVEL_STYLE[level]}`} data-testid="dunning-level-badge">
                {level}. Mahnung{level === 3 ? ' – Inkasso-Vorbereitung' : ''}
              </span>
              <span className="text-xs text-gray-500">Nächste Stufe wird automatisch vorgeschlagen</span>
            </div>

            <div className="rounded-lg bg-gray-50 p-4 grid sm:grid-cols-2 gap-x-6">
              <Row label="Rechnung" value={preview.invoice_number} testId="dunning-invoice-number" />
              <Row label="Kunde" value={preview.billing_name || '-'} />
              <Row label="Rechnungsdatum" value={preview.invoice_date} />
              <Row label="Rechnungsbetrag" value={`${eur(preview.invoice_amount)} €`} />
              {preview.paid_amount > 0 && <Row label="Bereits bezahlt" value={`-${eur(preview.paid_amount)} €`} />}
              <Row label="Offener Betrag" value={`${eur(preview.open_amount)} €`} testId="dunning-open-amount" />
              {level === 3 && (
                <Row label={`Verzugszinsen (${preview.interest_rate.toFixed(2).replace('.', ',')} %, ${preview.interest_days} Tage)`} value={`${eur(preview.interest_amount)} €`} testId="dunning-interest-amount" />
              )}
              <Row label="Gesamt fällig" value={`${eur(preview.total_due)} €`} strong testId="dunning-total-due" />
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Fällig seit</Label>
                <Input
                  type="date"
                  value={preview.due_date.split('.').reverse().join('-')}
                  onChange={(e) => e.target.value && setParams((p) => ({ ...p, due_date: e.target.value }))}
                  data-testid="dunning-due-date-input"
                />
              </div>
              <div>
                <Label className="text-xs">Neue Zahlungsfrist (Tage)</Label>
                <Input
                  type="number"
                  min="0"
                  value={params.payment_days ?? preview.payment_days}
                  onChange={(e) => setParams((p) => ({ ...p, payment_days: e.target.value }))}
                  data-testid="dunning-payment-days-input"
                />
                <p className="text-[11px] text-gray-500 mt-1">Zahlbar bis {preview.deadline}</p>
              </div>
              <div>
                <Label className="text-xs">Kundenart (Zinssatz § 288 BGB)</Label>
                <div className="flex mt-1 rounded-md border overflow-hidden text-xs">
                  {[false, true].map((isB2b) => (
                    <button
                      key={String(isB2b)}
                      type="button"
                      onClick={() => setParams((p) => ({ ...p, b2b: isB2b }))}
                      className={`flex-1 py-2 transition-colors ${preview.b2b === isB2b ? 'bg-[#9B1B30] text-white' : 'bg-white hover:bg-gray-50'}`}
                      data-testid={`dunning-b2b-${isB2b ? 'yes' : 'no'}`}
                    >
                      {isB2b ? 'Firma (+9 %)' : 'Privat (+5 %)'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs">Anschreiben</Label>
              <Textarea
                value={texts.intro}
                onChange={(e) => setTexts({ ...texts, intro: e.target.value, edited: true })}
                className="min-h-[130px] text-sm"
                data-testid="dunning-intro-textarea"
              />
            </div>

            <div>
              <Label className="text-xs flex items-center gap-1">
                <AlertTriangle className={`w-3.5 h-3.5 ${level === 3 ? 'text-red-600' : 'text-amber-600'}`} />
                {level === 3 ? 'Inkasso-Hinweis (rot hervorgehoben)' : 'Hinweis auf Verzugszinsen bei 3. Mahnung'}
              </Label>
              <Textarea
                value={texts.notice}
                onChange={(e) => setTexts({ ...texts, notice: e.target.value, edited: true })}
                className={`min-h-[100px] text-sm ${level === 3 ? 'border-red-300 bg-red-50/40' : 'border-amber-300 bg-amber-50/40'}`}
                data-testid="dunning-notice-textarea"
              />
              {texts.edited && (
                <button type="button" className="text-xs text-[#9B1B30] underline mt-1" onClick={() => setTexts({ intro: preview.default_intro, notice: preview.default_notice, edited: false })} data-testid="dunning-reset-texts-btn">
                  Standardtexte wiederherstellen
                </button>
              )}
            </div>

            <div className="flex gap-3 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} data-testid="dunning-cancel-btn">Abbrechen</Button>
              <Button onClick={create} disabled={creating || loading} className="flex-1 bg-[#9B1B30] hover:bg-[#7a1526] text-white" data-testid="dunning-create-btn">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileWarning className="w-4 h-4 mr-2" />}
                {level}. Mahnung erstellen
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DunningDialog;
